import { AccordionButton } from "@/components/Button/AccordionButton";
import ActionButton from "@/components/Button/ActionButton";
import { AttributeCard } from "@/components/Card/AttributeCard";
import Icon from "@/components/Icon/Icon";
import type { IconName } from "@/components/Icon/iconMap";
import { Grid, Spacer, VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useState } from "react";
import { match } from "ts-pattern";
import type { SoftwareType } from "../-const";
import { SOFTWARE_TYPE_TO_URL_MAP } from "./-hooks/useSoftwareType";

export const Route = createFileRoute("/_staff/software/add/")({
  component: Introduce,
});

type CommonProps = {
  selectedSoftwareType: SoftwareType | null;
  setSelectedSoftwareType: Dispatch<SetStateAction<SoftwareType | null>>;
};

function Introduce() {
  const [selectedSoftwareType, setSelectedSoftwareType] =
    useState<SoftwareType | null>(null);

  return (
    <VStack className="mx-auto w-[1000px] ">
      <Header />
      <Spacer variant="xs" />
      <SoftwareTypeRadioGroup
        selectedSoftwareType={selectedSoftwareType}
        setSelectedSoftwareType={setSelectedSoftwareType}
      />
      <Spacer variant="xxl" />
      <AdditionalOptions
        selectedSoftwareType={selectedSoftwareType}
        setSelectedSoftwareType={setSelectedSoftwareType}
      />
    </VStack>
  );
}

function Header() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.add.header",
  });

  return (
    <VStack>
      <Spacer variant="xxl" />
      <Text variant="h2">{t("headerTitle")}</Text>
    </VStack>
  );
}

function SoftwareTypeRadioGroup({
  selectedSoftwareType,
  setSelectedSoftwareType,
}: CommonProps) {
  const navigate = useNavigate();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.add.body",
  });
  const { t: tSoftwareProfile } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.profile",
  });
  const { t: tSoftwareType } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.add.softwareTypes",
  });
  const { t: tSoftwareAdd } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.customSaas.add.form.navigation.step1",
  });

  const allSoftwareTypes: {
    id: SoftwareType;
    title: string;
    requiredDetails: { iconName: IconName; text: string }[];
  }[] = [
    {
      id: "CUSTOM_SAAS",
      title: tSoftwareType("customSaas"),
      requiredDetails: [
        { iconName: "SquareDot", text: tSoftwareAdd("customSaasNameLabel") },
      ],
    },
    {
      id: "INSTALLED",
      title: tSoftwareType("installed"),
      requiredDetails: [
        {
          iconName: "SquareDot",
          text: tSoftwareProfile(
            "detail.sections.softwareIdInformation.softwareName",
          ),
        },
      ],
    },
    {
      id: "MW_OS",
      title: tSoftwareType("mwOs"),
      requiredDetails: [
        {
          iconName: "SquareDot",
          text: tSoftwareProfile(
            "detail.sections.softwareIdInformation.softwareName",
          ),
        },
      ],
    },
    {
      id: "SCRATCH",
      title: tSoftwareType("scratch"),
      requiredDetails: [
        {
          iconName: "SquareDot",
          text: tSoftwareProfile(
            "detail.sections.softwareIdInformation.softwareName",
          ),
        },
      ],
    },
    // v0.26では非表示
    // {
    //   id: "PRODUCT",
    //   title: tSoftwareType("product"),
    //   requiredDetails: [
    //     {
    //       iconName: "SquareDot",
    //       text: tSoftwareProfile(
    //         "detail.sections.softwareIdInformation.softwareName",
    //       ),
    //     },
    //   ],
    // },
  ];

  const handleRadioChange = useCallback(
    (value: SoftwareType) => {
      setSelectedSoftwareType(value);
    },
    [setSelectedSoftwareType],
  );

  const handleActionButtonClick = useCallback(() => {
    if (selectedSoftwareType === "CUSTOM_SAAS") {
      navigate({
        to: "/software/custom-saas/add/$customSaasType",
        params: { customSaasType: "CUSTOM_SAAS" },
        hash: "basic",
      });
    } else if (
      selectedSoftwareType &&
      selectedSoftwareType in SOFTWARE_TYPE_TO_URL_MAP
    ) {
      navigate({
        to: "/software/add/$type",
        params: {
          type: SOFTWARE_TYPE_TO_URL_MAP[
            selectedSoftwareType as keyof typeof SOFTWARE_TYPE_TO_URL_MAP
          ],
        },
      });
    }
  }, [selectedSoftwareType, navigate]);

  return (
    <>
      <Spacer variant="xl" />
      <Text variant="h4">{t("selectSoftwareType")}</Text>
      <Spacer variant="l" />
      <RadioGroup onValueChange={handleRadioChange}>
        <Grid columns={2} rowGap={28} columnGap={24}>
          {allSoftwareTypes.map((type) => (
            <AttributeCard
              key={type.id}
              title={type.title}
              titleStyle="CardHeader"
              onClick={() => handleRadioChange(type.id)}
              actionButton={
                <RadioGroupItem
                  id={type.id}
                  value={type.id}
                  className="h-4 w-4"
                  checked={selectedSoftwareType === type.id}
                />
              }
              actionButtonPosition="top-left"
              actionButtonIsInner={false}
              className="cursor-pointer"
            >
              <VStack className="gap-3">
                <Text variant="labelSmall">
                  {t("itemCard.requiredDetails")}
                </Text>
                {type.requiredDetails.map((detail, index) => (
                  <Icon
                    key={`${detail.text}-${index}`}
                    iconName={detail.iconName}
                    text={detail.text}
                  />
                ))}
              </VStack>
            </AttributeCard>
          ))}
        </Grid>
      </RadioGroup>
      <Spacer variant="xl" />
      <ActionButton
        icon="FilePenIcon"
        className="w-48"
        disabled={!selectedSoftwareType}
        onClick={handleActionButtonClick}
      >
        {t("fillOutDetails")}
      </ActionButton>
    </>
  );
}

function AdditionalOptions({ selectedSoftwareType }: CommonProps) {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.add.options",
  });
  const { t: tSoftwareType } = useTranslation(defaultNS, {
    keyPrefix: "pages.software.add.softwareTypes",
  });

  const displaySoftwareType = match(selectedSoftwareType)
    .with("CUSTOM_SAAS", () => tSoftwareType("customSaas"))
    .with("INSTALLED", () => tSoftwareType("installed"))
    .with("MW_OS", () => tSoftwareType("mwOs"))
    .with("SCRATCH", () => tSoftwareType("scratch"))
    .with("PRODUCT", () => tSoftwareType("product"))
    .otherwise(() => "");

  return (
    <VStack className="w-full">
      <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
        {t("description")}
      </Text>
      <Spacer size={24} />
      <AccordionButton
        icon={
          <Icon
            iconName="DatabaseBackup"
            text={t("bulkCsvUpload.title")}
            textColor="text-primary"
            colorVariant="primary"
          />
        }
        className="w-full"
        // v0.26では未実装
        inDevelopment
      >
        <VStack className="gap-2">
          <div className="flex flex-col items-start gap-2 text-neutral-on-fill">
            <Text variant="p">{t("bulkCsvUpload.description")}</Text>
            <Text variant="label">{`${t(
              "bulkCsvUpload.selectedSoftwareType",
            )} ${displaySoftwareType}`}</Text>
          </div>
          <ActionButton
            size="sm"
            variant="default"
            icon="ArrowRightIcon"
            iconPosition="right"
          >
            {t("bulkCsvUpload.button")}
          </ActionButton>
        </VStack>
      </AccordionButton>
    </VStack>
  );
}
